import type { KataId } from '../../data/catalog/types'
import { getKataScoring, RULE_TABLE_VERSION } from '../../data/scoring'
import { scorePerformance } from '../../lib/scoring/engine'
import { getDb } from '../db'
import type { KataPerformance } from '../schema'

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `perf-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function recompute(performance: KataPerformance): KataPerformance {
  const scoring = getKataScoring(performance.kataId)
  if (!scoring) return performance
  const result = scorePerformance(
    scoring.elements,
    performance.elementMarks,
    scoring.maxPerJudge,
  )
  return {
    ...performance,
    rawTotal: result.rawTotal,
    forgottenPenaltyApplied: result.forgottenPenaltyApplied,
    finalTotal: result.finalTotal,
    maxTotal: result.maxTotal,
  }
}

export async function listPerformancesForKata(kataId: KataId): Promise<KataPerformance[]> {
  const db = await getDb()
  const rows = await db.getAllFromIndex('performances', 'by-kata', kataId)
  return rows.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export async function getPerformance(id: string): Promise<KataPerformance | undefined> {
  const db = await getDb()
  return db.get('performances', id)
}

export async function createPerformance(kataId: KataId): Promise<KataPerformance> {
  const scoring = getKataScoring(kataId)
  if (!scoring) {
    throw new Error(`Scoring is not available for ${kataId}`)
  }
  const now = new Date().toISOString()
  const performance = recompute({
    id: newId(),
    kataId,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
    ruleTableVersion: RULE_TABLE_VERSION,
    judgeCount: 1,
    elementMarks: [],
    rawTotal: scoring.maxPerJudge,
    forgottenPenaltyApplied: false,
    finalTotal: scoring.maxPerJudge,
    maxTotal: scoring.maxPerJudge,
  })
  const db = await getDb()
  await db.put('performances', performance)
  return performance
}

export async function savePerformance(performance: KataPerformance): Promise<KataPerformance> {
  const updated = recompute({
    ...performance,
    updatedAt: new Date().toISOString(),
  })
  const db = await getDb()
  await db.put('performances', updated)
  return updated
}

export async function completePerformance(
  performance: KataPerformance,
  extras?: { scoredBy?: string; note?: string },
): Promise<KataPerformance> {
  const now = new Date().toISOString()
  return savePerformance({
    ...performance,
    ...extras,
    status: 'completed',
    completedAt: now,
  })
}

export async function deletePerformance(id: string): Promise<void> {
  const db = await getDb()
  await db.delete('performances', id)
}

export async function findLatestDraft(kataId: KataId): Promise<KataPerformance | undefined> {
  const rows = await listPerformancesForKata(kataId)
  return rows.find((r) => r.status === 'draft')
}
